import { squareLabel } from "./board-view-state.js";

const PIECES = Object.freeze({
  wk: "♔︎",
  wq: "♕︎",
  wr: "♖︎",
  wb: "♗︎",
  wn: "♘︎",
  wp: "♙︎",
  bk: "♚︎",
  bq: "♛︎",
  br: "♜︎",
  bb: "♝︎",
  bn: "♞︎",
  bp: "♟︎",
});

export class DomBoardView {
  constructor(callbacks) {
    this.callbacks = callbacks;
    this.element = document.createElement("div");
    this.element.className = "board";
    this.element.setAttribute("role", "grid");
    this.element.setAttribute("aria-label", "Chess board");
    this.state = null;
    this.drag = null;
    this.dragGhost = null;
    this.suppressClick = false;
    this.onClick = (event) => this.#click(event);
    this.onPointerDown = (event) => this.#pointerDown(event);
    this.onPointerMove = (event) => this.#pointerMove(event);
    this.onPointerUp = (event) => this.#pointerUp(event);
    this.onPointerCancel = () => this.#cancelDrag();
    this.element.addEventListener("click", this.onClick);
    this.element.addEventListener("pointerdown", this.onPointerDown);
    this.element.addEventListener("pointermove", this.onPointerMove);
    this.element.addEventListener("pointerup", this.onPointerUp);
    this.element.addEventListener("pointercancel", this.onPointerCancel);
  }

  mount(container) {
    container.replaceChildren(this.element);
  }

  update(state) {
    this.state = state;
    this.element.dataset.orientation = state.orientation;
    if (state.boardTextureUrl) {
      this.element.style.setProperty(
        "--board-texture",
        `url("${state.boardTextureUrl.replaceAll('"', "%22")}")`,
      );
    } else {
      this.element.style.removeProperty("--board-texture");
    }
    this.element.innerHTML = state.squares
      .map((square) => this.#squareTemplate(square, state))
      .join("");
    this.element.dataset.pieceCount = String(state.pieces.length);
  }

  focusSquare(square) {
    const target = /** @type {HTMLElement | null} */ (
      this.element.querySelector(`[data-square="${square}"]`)
    );
    target?.focus({ preventScroll: true });
  }

  async animateMove(move, options = {}) {
    if (
      options.actor !== "opponent" ||
      this.state?.reducedMotion ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    const source = this.element.querySelector(`[data-square="${move.from}"]`);
    const target = this.element.querySelector(`[data-square="${move.to}"]`);
    const piece = source?.querySelector(".piece");
    if (!source || !target || !piece) {
      return;
    }
    const from = source.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const deltaX = to.left - from.left;
    const deltaY = to.top - from.top;
    const distance = Math.hypot(deltaX, deltaY);
    const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
    const trail = document.createElement("div");
    trail.className = "computer-move-trail";
    trail.dataset.from = move.from;
    trail.dataset.to = move.to;
    Object.assign(trail.style, {
      left: `${from.left + from.width / 2}px`,
      top: `${from.top + from.height / 2}px`,
      width: `${distance}px`,
      transform: `rotate(${angle}deg)`,
    });
    const ghost = document.createElement("div");
    ghost.className = "computer-move-ghost";
    ghost.dataset.from = move.from;
    ghost.dataset.to = move.to;
    ghost.setAttribute("aria-hidden", "true");
    Object.assign(ghost.style, {
      left: `${from.left}px`,
      top: `${from.top}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
    });
    ghost.append(piece.cloneNode(true));
    source.classList.add("computer-move-source");
    target.classList.add("computer-move-target");
    document.body.append(trail, ghost);
    try {
      const movement = ghost.animate(
        [
          { transform: "translate3d(0, 0, 0) scale(1)" },
          {
            transform: `translate3d(${deltaX * 0.52}px, ${deltaY * 0.52}px, 0) scale(1.14)`,
            offset: 0.55,
          },
          {
            transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(1)`,
          },
        ],
        {
          duration: 560,
          easing: "cubic-bezier(.22,.78,.22,1)",
          fill: "forwards",
        },
      );
      const trailAnimation = trail.animate(
        [
          { opacity: 0, clipPath: "inset(0 100% 0 0)" },
          { opacity: 0.8, clipPath: "inset(0 0 0 0)", offset: 0.55 },
          { opacity: 0.18, clipPath: "inset(0 0 0 0)" },
        ],
        { duration: 560, easing: "ease-out", fill: "forwards" },
      );
      await Promise.race([
        Promise.allSettled([movement.finished, trailAnimation.finished]),
        new Promise((resolve) => setTimeout(resolve, 900)),
      ]);
    } finally {
      source.classList.remove("computer-move-source");
      target.classList.remove("computer-move-target");
      ghost.remove();
      trail.remove();
    }
  }

  dispose() {
    this.#cancelDrag();
    this.element.removeEventListener("click", this.onClick);
    this.element.removeEventListener("pointerdown", this.onPointerDown);
    this.element.removeEventListener("pointermove", this.onPointerMove);
    this.element.removeEventListener("pointerup", this.onPointerUp);
    this.element.removeEventListener("pointercancel", this.onPointerCancel);
    this.element.remove();
  }

  #squareTemplate(square, state) {
    const piece = state.pieces.find((item) => item.square === square);
    const legal = state.legalMoves.find((move) => move.to === square);
    const hintSource = state.hint?.stage >= 2 && state.hint.from === square;
    const hintTarget = state.hint?.stage >= 3 && state.hint.to === square;
    const classes = [
      squareColor(square),
      legal && state.legalHighlights ? "legal" : "",
      legal?.captured ? "capture" : "",
      state.selectedSquare === square ? "selected" : "",
      state.lastMove &&
      (state.lastMove.from === square || state.lastMove.to === square)
        ? "last-move"
        : "",
      state.lastMoveWasEngine && state.lastMove?.to === square
        ? "engine-arrival"
        : "",
      state.checkSquare === square ? "in-check" : "",
      hintSource ? "hint-source" : "",
      hintTarget ? "hint-target" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const file = square[0];
    const rank = square[1];
    const showFile =
      state.coordinates &&
      ((state.orientation === "w" && rank === "1") ||
        (state.orientation === "b" && rank === "8"));
    const showRank =
      state.coordinates &&
      ((state.orientation === "w" && file === "a") ||
        (state.orientation === "b" && file === "h"));
    const pieceMarkup = piece
      ? piece.imageUrl
        ? `<img class="piece custom-piece" src="${escapeHtml(piece.imageUrl)}" alt="" aria-hidden="true">`
        : `<span class="piece ${piece.color === "w" ? "white-piece" : "black-piece"}" aria-hidden="true">${PIECES[piece.color + piece.type]}</span>`
      : "";
    return `<button class="square ${classes}" role="gridcell" data-square="${square}" aria-label="${escapeHtml(squareLabel(square, state))}" aria-selected="${state.selectedSquare === square}" tabindex="${square === state.focusedSquare ? "0" : "-1"}">${pieceMarkup}${hintSource ? '<span class="hint-marker" aria-hidden="true">1</span>' : ""}${hintTarget ? '<span class="hint-marker target" aria-hidden="true">2</span>' : ""}${showFile ? `<span class="coordinate file">${file}</span>` : ""}${showRank ? `<span class="coordinate rank">${rank}</span>` : ""}</button>`;
  }

  #click(event) {
    const square = event.target.closest("[data-square]")?.dataset.square;
    if (!square) {
      return;
    }
    if (this.suppressClick) {
      this.suppressClick = false;
      return;
    }
    this.callbacks.onSquareActivate(square);
  }

  #pointerDown(event) {
    const squareElement = event.target.closest("[data-square]");
    const square = squareElement?.dataset.square;
    const piece = this.state?.pieces.find((item) => item.square === square);
    if (!square || !piece || !this.state?.interactive) {
      return;
    }
    if (
      piece.color !== this.state.turn ||
      (!this.state.localMode && piece.color !== this.state.playerSide)
    ) {
      return;
    }
    this.drag = {
      from: square,
      pointerId: event.pointerId,
      start: { x: event.clientX, y: event.clientY },
      moved: false,
      moves: this.callbacks.getLegalMoves(square),
    };
    try {
      squareElement.setPointerCapture?.(event.pointerId);
    } catch {
      // Synthetic accessibility tooling may not register an active pointer.
    }
  }

  #pointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) {
      return;
    }
    const distance = Math.hypot(
      event.clientX - this.drag.start.x,
      event.clientY - this.drag.start.y,
    );
    if (!this.drag.moved && distance >= 6) {
      this.drag.moved = true;
      this.#startDragVisual(this.drag.from, event);
    }
    if (this.drag.moved) {
      event.preventDefault();
      this.#positionDragGhost(event);
      this.element
        .querySelectorAll(".square.drag-over")
        .forEach((item) => item.classList.remove("drag-over"));
      document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest?.(".square.legal")
        ?.classList.add("drag-over");
    }
  }

  #pointerUp(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) {
      return;
    }
    const { from, moved } = this.drag;
    const target = /** @type {HTMLElement | null} */ (
      document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest?.("[data-square]")
    )?.dataset.square;
    this.#cancelDrag();
    if (moved) {
      this.suppressClick = true;
      setTimeout(() => {
        this.suppressClick = false;
      });
    }
    if (moved && target && target !== from) {
      this.callbacks.onMoveIntent({ from, to: target });
    }
  }

  #startDragVisual(square, event) {
    const source = this.element.querySelector(`[data-square="${square}"]`);
    const piece = source?.querySelector(".piece");
    if (!source || !piece) {
      return;
    }
    source.classList.add("selected", "drag-source");
    for (const move of this.drag?.moves ?? []) {
      const destination = this.element.querySelector(
        `[data-square="${move.to}"]`,
      );
      destination?.classList.add("legal");
      if (move.captured) {
        destination?.classList.add("capture");
      }
    }
    const bounds = source.getBoundingClientRect();
    const ghost = document.createElement("div");
    ghost.className = "drag-ghost";
    ghost.setAttribute("aria-hidden", "true");
    ghost.style.width = `${bounds.width}px`;
    ghost.style.height = `${bounds.height}px`;
    ghost.append(piece.cloneNode(true));
    document.body.append(ghost);
    this.dragGhost = ghost;
    this.#positionDragGhost(event);
  }

  #positionDragGhost(event) {
    if (this.dragGhost) {
      this.dragGhost.style.left = `${event.clientX}px`;
      this.dragGhost.style.top = `${event.clientY}px`;
    }
  }

  #cancelDrag() {
    this.dragGhost?.remove();
    this.dragGhost = null;
    this.drag = null;
    this.element
      .querySelectorAll(
        ".square.drag-source, .square.drag-over, .square.legal, .square.capture",
      )
      .forEach((square) =>
        square.classList.remove(
          "drag-source",
          "drag-over",
          "selected",
          "legal",
          "capture",
        ),
      );
  }
}

function squareColor(square) {
  return (square.charCodeAt(0) - 97 + Number(square[1])) % 2 === 1
    ? "dark"
    : "light";
}

function escapeHtml(value) {
  return String(value).replace(
    /[&<>\"]/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[character],
  );
}
